import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Plus, Trash2, Tv, DownloadCloud, Ban } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'
import {
  fetchIptvChannels,
  importChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  type IptvChannelRow,
} from './mediaApi'

type EditTarget = IptvChannelRow | 'new' | null

/**
 * TV.01 — IPTV kanallar (tz/mp3-tv). GET /admin/tv/channels; «Import» → m-kino
 * ro'yxatini tortadi (upsert); qo'shish/tahrir → POST/PUT; premium-flag + o'chirish.
 */
export function IptvChannels() {
  const { toast } = useToast()
  const [target, setTarget] = useState<EditTarget>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [importing, setImporting] = useState(false)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast('Готово', { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const doImport = async () => {
    setImporting(true)
    try {
      const r = await importChannels()
      toast(`Импортировано: ${r.imported}, обновлено: ${r.updated}`, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast('Импорт не удался', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setImporting(false)
    }
  }

  const columns = useMemo<AdminColumn<IptvChannelRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Канал',
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Tv size={14} className="text-tertiary" />
            <span className="font-medium text-primary">{r.name}</span>
            {r.is_premium ? <span className="text-xs text-amber-500">Premium</span> : null}
          </span>
        ),
      },
      { key: 'country', header: 'Страна', type: 'text', width: 90 },
      { key: 'category', header: 'Категория', type: 'text', width: 120 },
      {
        key: 'enabled',
        header: 'Статус',
        type: 'text',
        width: 110,
        render: (r) => <StatusBadge status={r.enabled ? 'active' : 'deleted'} label={r.enabled ? 'Вкл' : 'Выкл'} />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<IptvChannelRow>[]>(
    () => [
      { id: 'edit', label: 'Изменить', icon: <Pencil size={13} />, onSelect: (r) => setTarget(r) },
      { id: 'delete', label: 'Удалить', icon: <Trash2 size={13} />, danger: true, onSelect: (r) => void run(() => deleteChannel(r.id)) },
    ],
    [run],
  )

  return (
    <>
      <PageHeader
        title="ТВ каналы"
        screenId="TV.01"
        actions={
          <div className="flex gap-2">
            <Link to="/media/tv/blocked">
              <Button size="sm" variant="ghost">
                <Ban size={14} /> Davlatlar
              </Button>
            </Link>
            <Button size="sm" variant="ghost" onClick={doImport} disabled={importing}>
              <DownloadCloud size={14} /> {importing ? 'Импорт…' : 'Импорт'}
            </Button>
            <Button size="sm" onClick={() => setTarget('new')}>
              <Plus size={14} /> Добавить
            </Button>
          </div>
        }
      />
      <DataTable<IptvChannelRow>
        key={reloadKey}
        screenId="TV.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchIptvChannels}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Tv size={22} />, title: 'Каналы (нажмите «Импорт»)' }}
      />
      {target && (
        <ChannelDialog
          target={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function ChannelDialog({ target, onClose, onSaved }: { target: EditTarget; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast()
  const editing = target !== 'new' && target !== null ? target : null
  const [name, setName] = useState(editing?.name ?? '')
  const [country, setCountry] = useState(editing?.country ?? '')
  const [category, setCategory] = useState(editing?.category ?? 'general')
  const [url, setUrl] = useState(editing?.stream_url ?? '')
  const [logo, setLogo] = useState(editing?.logo_url ?? '')
  const [premium, setPremium] = useState(editing?.is_premium ?? false)
  const [enabled, setEnabled] = useState(editing?.enabled ?? true)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!name.trim() || !url.trim()) return
    setBusy(true)
    try {
      const body = {
        name: name.trim(),
        country: country.trim().toUpperCase(),
        category: category.trim() || 'general',
        stream_url: url.trim(),
        logo_url: logo.trim(),
        is_premium: premium,
        enabled,
      }
      if (editing) await updateChannel(editing.id, body)
      else await createChannel(body)
      toast('Готово', { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast('Ошибка', { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? 'Изменить канал' : 'Новый канал'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={save} disabled={busy || !name.trim() || !url.trim()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Название</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Поток (m3u8 URL)</span>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} mono placeholder="https://…/playlist.m3u8" />
        </label>
        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">Страна</span>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="UZ" />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-secondary">Категория</span>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="general" />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Логотип (URL)</span>
          <Input value={logo} onChange={(e) => setLogo(e.target.value)} mono />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={premium} onChange={(e) => setPremium(e.target.checked)} />
          <span className="text-secondary">Premium канал</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="text-secondary">Включён</span>
        </label>
      </div>
    </Dialog>
  )
}
